import type { EventDto, EventSlotDto } from '@/types/dtos/events/response/event.dto';
import type { FlowDto, FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import { getFlowModerators as getFlowModeratorsUtil } from '@/utils/contributor-extraction.utils';
import {
  getSessionContentId,
  getSessionSegmentSlots,
  isSegmentedSession,
} from '@/components/flows/detail/slot-list/session-segment.helpers';

export const getFlowDayNumber = (flow: FlowDto, event: EventDto | null | undefined): number | null => {
  if (!event?.slots || !event?.days) {
    return null;
  }

  const flowSlotInEvent = event.slots.find((slot) => slot.content === flow.id);

  if (!flowSlotInEvent) {
    return null;
  }

  const day = event.days.find((day) => day.id === flowSlotInEvent.day.id);

  if (!day) {
    return null;
  }

  return event.days.indexOf(day) + 1;
};

export const getFlowDayAndTimeInfo = (
  flow: FlowDto,
  event: EventDto | null | undefined,
  eventSlot?: EventSlotDto,
): string | null => {
  if (!event?.slots || !event?.days || !event?.startDate) {
    return null;
  }

  // Use provided eventSlot if available, otherwise find the first matching one
  const flowSlotInEvent = eventSlot || event.slots.find((slot) => slot.content === flow.id);

  if (!flowSlotInEvent) {
    return null;
  }

  const day = event.days.find((day) => day.id === flowSlotInEvent.day.id);

  if (!day) {
    return null;
  }

  const dayNumber = event.days.indexOf(day) + 1;

  // Calculate time range
  const startOffsetMinutes = flowSlotInEvent.startOffsetMinutes;
  const slotStartTime = minuteOffsetToTime(startOffsetMinutes, event.startDate);

  // Determine duration: use plannedDuration if set, otherwise use computedDuration
  // Priority: slot.durationPlannedMinutes > flow.durationPlannedMinutes > flow.computedDurationMinutes
  const durationMinutes =
    flowSlotInEvent.durationPlannedMinutes ?? flow.durationPlannedMinutes ?? flow.computedDurationMinutes;

  if (durationMinutes === undefined) {
    // No duration available, return just day and start time
    return `Day ${dayNumber} | ${slotStartTime}`;
  }

  const endOffsetMinutes = startOffsetMinutes + durationMinutes;
  const slotEndTime = minuteOffsetToTime(endOffsetMinutes, event.startDate);

  const timeRange = `${slotStartTime} - ${slotEndTime}`;
  return `Day ${dayNumber} | ${timeRange}`;
};

/** Clock times only for a flow content slot (no day / flow title); used in flow-scoped contributor list. */
export const getFlowSlotTimeRangeOnly = (
  event: EventDto,
  flowSlotInEvent: EventSlotDto,
  contentSlotInFlow: FlowSlotDto,
): { start: string; end?: string } | null => {
  if (!event.startDate || !flowSlotInEvent.day || !event.days) {
    return null;
  }

  const day = event.days.find((d) => d.id === flowSlotInEvent.day.id);
  if (!day) return null;

  const slotOffsetMinutes = contentSlotInFlow?.computedOffsetMinutes ?? contentSlotInFlow?.plannedOffsetMinutes ?? null;
  if (slotOffsetMinutes === null) {
    return null;
  }

  const startingOffsetInDay = flowSlotInEvent.startOffsetMinutes;
  const slotStartOffsetFromEventStart = startingOffsetInDay + slotOffsetMinutes;
  const slotStartTime = minuteOffsetToTime(slotStartOffsetFromEventStart, event.startDate);

  const slotDuration = contentSlotInFlow?.computedDurationMinutes ?? contentSlotInFlow?.plannedDurationMinutes;
  if (slotDuration === undefined) {
    return { start: slotStartTime };
  }

  const slotEndOffsetFromEventStart = slotStartOffsetFromEventStart + slotDuration;
  const slotEndTime = minuteOffsetToTime(slotEndOffsetFromEventStart, event.startDate);

  return { start: slotStartTime, end: slotEndTime };
};

/** First-segment start through last-segment end when the session is split across multiple flow slots. */
export const getSegmentedSessionTimeRange = (
  event: EventDto,
  flowSlotInEvent: EventSlotDto,
  flow: FlowDto,
  contentSlotInFlow: FlowSlotDto,
): { start: string; end?: string } | null => {
  const sessionContentId = getSessionContentId(contentSlotInFlow);
  if (!sessionContentId || !isSegmentedSession(flow, contentSlotInFlow)) {
    return getFlowSlotTimeRangeOnly(event, flowSlotInEvent, contentSlotInFlow);
  }

  const segments = getSessionSegmentSlots(flow, sessionContentId);
  const first = segments[0];
  const last = segments[segments.length - 1];
  if (!first || !last) {
    return getFlowSlotTimeRangeOnly(event, flowSlotInEvent, contentSlotInFlow);
  }

  const firstRange = getFlowSlotTimeRangeOnly(event, flowSlotInEvent, first);
  const lastRange = getFlowSlotTimeRangeOnly(event, flowSlotInEvent, last);
  if (!firstRange) return null;

  return { start: firstRange.start, end: lastRange?.end };
};

export const getFlowSlotTiming = (
  event: EventDto,
  flowSlotInEvent: EventSlotDto,
  flow: FlowDto,
  contentSlotInFlow: FlowSlotDto,
) => {
  if (!flowSlotInEvent.day || !event.days) {
    return 'Timing unavailable';
  }

  const day = event.days.find((day) => day.id === flowSlotInEvent.day.id);
  if (day) {
    const dayNumber = event.days.indexOf(day) + 1;

    if (!event.startDate) {
      return `Day ${dayNumber}`;
    }

    const range = getSegmentedSessionTimeRange(event, flowSlotInEvent, flow, contentSlotInFlow);
    if (!range) {
      return `Day ${dayNumber}`;
    }
    if (range.end === undefined) {
      return `Day ${dayNumber} | ${range.start}`;
    }
    return `Day ${dayNumber} | ${range.start} - ${range.end}`;
  }

  return 'Timing unavailable';
};

export const getFlowModerators = (
  flow: FlowDto,
  allContributors: ContributorDto[] = [],
  eventMultiFlow?: boolean,
): string => {
  // Use the new utility function to get moderators
  const moderators = getFlowModeratorsUtil(flow, allContributors, eventMultiFlow)
    .map((contributor) => {
      const profile = contributor.profiles?.[0];
      return profile?.displayName || contributor.email || 'Unknown';
    })
    .filter(Boolean);

  return moderators.join(', ');
};
